import React, { useState } from 'react';
import { BiCheckCircle } from 'react-icons/bi';
import { AiOutlineClose, AiOutlineUnorderedList, AiOutlineCreditCard } from 'react-icons/ai';
import { formatCurrency } from '../utils/CurrencyUtils';
import { formatDate } from '../utils/DateUtils';
import ModalPortal from './lib/modalPortal';
import { extractOrderItems, getStudentName } from '../utils/StringUtils';

export default function RegistryOrdersInfo({
    registryOrders,
    showRegistryOrdersInfoModal,
    handleCancelClick,
}) {
    const [showPopover, setShowPopover] = useState({ type: null, visible: false });

    if (!showRegistryOrdersInfoModal || !registryOrders) return null;

    const parsedOrders = registryOrders.orders.map((order) => {
        const paymentsMap = {};
        order.transactions.forEach((tx) => {
            const key = tx.paymentMethod.replace('Pedido - ', '');
            paymentsMap[key] = (paymentsMap[key] || 0) + tx.value;
        });
        return {
            orderId: order.orderId,
            clientId: order.clientId,
            purchaseDate: order.purchaseDate,
            totalValue: order.transactions.reduce((sum, tx) => sum + tx.value, 0),
            payments: paymentsMap,
            itemsList: order.itemsList,
        };
    });

    const numPedidos = registryOrders.orders.length;
    const numRecargas = registryOrders.orders.flatMap(o => o.transactions)
        .filter(tx => tx.transactionType !== 'Order').length;

    const totalPedidos = registryOrders.orders.reduce((sum, order) => {
        return sum + order.transactions
            .filter(tx => tx.transactionType === 'Order')
            .reduce((s, tx) => s + tx.value, 0);
    }, 0);

    const totalPorItem = {};
    registryOrders.orders.forEach(order => {
        extractOrderItems(order.itemsList).forEach(item => {
            totalPorItem[item.name] = (totalPorItem[item.name] || 0) + item.quantity;
        });
    });

    const totalPorMetodo = {};
    registryOrders.orders.forEach(order => {
        order.transactions.forEach(tx => {
            const key = tx.paymentMethod.replace('Pedido - ', '');
            totalPorMetodo[key] = (totalPorMetodo[key] || 0) + tx.value;
        });
    });

    return (
        <ModalPortal>
            <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
                <div className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh] animate-in zoom-in duration-200 overflow-hidden">
                    
                    {/* Header */}
                    <div className="flex justify-between items-center p-5 border-b border-slate-100 bg-slate-50">
                        <h2 className="text-xl font-bold text-gray-800">Detalhes da Lista de Pedidos</h2>
                        <button onClick={handleCancelClick} className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-slate-200 transition-colors">
                            <AiOutlineClose size={20} />
                        </button>
                    </div>

                    {/* Content Scrollable */}
                    <div className="p-6 overflow-y-auto custom-scrollbar flex-1 space-y-6">
                        
                        {/* Resumo Topo */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-200">
                            <div>
                                <p className="text-xs font-bold text-gray-500 uppercase">Abertura</p>
                                <p className="font-semibold text-sm">{formatDate(registryOrders.openingTime)}</p>
                            </div>
                            <div>
                                <p className="text-xs font-bold text-gray-500 uppercase">Fechamento</p>
                                <p className="font-semibold text-sm">{formatDate(registryOrders.closingTime)}</p>
                            </div>
                            <div>
                                <p className="text-xs font-bold text-gray-500 uppercase">Qtd. Pedidos/Recargas</p>
                                <p className="font-semibold text-sm">{numPedidos} / {numRecargas}</p>
                            </div>
                            <div>
                                <p className="text-xs font-bold text-gray-500 uppercase">Total Pedidos</p>
                                <p className="font-bold text-green-600 text-lg">{formatCurrency(totalPedidos)}</p>
                            </div>
                        </div>

                        {/* Botões de Totais (Popover) */}
                        <div className="flex gap-3">
                            <div className="relative w-full"
                                onMouseEnter={() => setShowPopover({ type: 'item', visible: true })}
                                onMouseLeave={() => setShowPopover({ type: 'item', visible: false })}>
                                <button className="w-full flex items-center justify-center gap-2 bg-blue-50 text-blue-700 font-bold py-2 px-4 rounded-lg border border-blue-100 hover:bg-blue-100 transition-colors">
                                    <AiOutlineUnorderedList /> Quantidade por Item
                                </button>
                                {showPopover.visible && showPopover.type === 'item' && (
                                    <div className="absolute z-50 top-full mt-2 left-0 w-full bg-white border border-slate-200 rounded-xl shadow-xl p-4 animate-in fade-in slide-in-from-top-2 duration-200">
                                        <h4 className="font-bold text-gray-700 text-sm mb-2 border-b pb-1">Total por Item</h4>
                                        <ul className="text-sm space-y-1 max-h-48 overflow-y-auto custom-scrollbar">
                                            {Object.entries(totalPorItem).map(([key, value]) => (
                                                <li key={key} className="flex justify-between"><span className="text-gray-600">{key}</span><span className="font-bold text-gray-800">{value}</span></li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>

                            <div className="relative w-full"
                                onMouseEnter={() => setShowPopover({ type: 'method', visible: true })}
                                onMouseLeave={() => setShowPopover({ type: 'method', visible: false })}>
                                <button className="w-full flex items-center justify-center gap-2 bg-green-50 text-green-700 font-bold py-2 px-4 rounded-lg border border-green-100 hover:bg-green-100 transition-colors">
                                    <AiOutlineCreditCard /> Total por Método
                                </button>
                                {showPopover.visible && showPopover.type === 'method' && (
                                    <div className="absolute z-50 top-full mt-2 left-0 w-full bg-white border border-slate-200 rounded-xl shadow-xl p-4 animate-in fade-in slide-in-from-top-2 duration-200">
                                        <h4 className="font-bold text-gray-700 text-sm mb-2 border-b pb-1">Total por Método</h4>
                                        <ul className="text-sm space-y-1">
                                            {Object.entries(totalPorMetodo).map(([key, value]) => (
                                                <li key={key} className="flex justify-between"><span className="text-gray-600">{key}</span><span className="font-bold text-gray-800">{formatCurrency(value)}</span></li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Lista de Pedidos */}
                        <div className="space-y-4">
                            {parsedOrders.map((order, idx) => (
                                <div key={idx} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
                                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 border-b border-slate-100 pb-2">
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <span className="font-bold text-gray-800">Pedido #{order.orderId}</span>
                                                <span className="text-xs text-gray-400 font-mono">{formatDate(order.purchaseDate)}</span>
                                            </div>
                                            <p className="text-xs text-gray-500">Cliente: <span className="font-semibold text-gray-700">{getStudentName(order.clientId)}</span></p>
                                        </div>
                                        <p className="font-bold text-green-600 text-lg mt-2 sm:mt-0">{formatCurrency(order.totalValue)}</p>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <p className="text-xs font-bold text-gray-400 uppercase mb-1">Itens</p>
                                            <ul className="space-y-1">
                                                {extractOrderItems(order.itemsList).map((itemDetail, idx2) => (
                                                    <li key={idx2} className="flex justify-between bg-slate-50 px-2 py-1 rounded border border-slate-100">
                                                        <span className="text-gray-700">{itemDetail.name}</span>
                                                        <span className="font-mono text-gray-500 text-xs">x{itemDetail.quantity}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold text-gray-400 uppercase mb-1">Pagamento</p>
                                            <ul className="space-y-1">
                                                {Object.entries(order.payments).map(([method, value]) => (
                                                    <li key={method} className="flex justify-between px-2 py-1">
                                                        <span className="text-gray-600">{method}</span>
                                                        <span className="font-bold text-gray-800">{formatCurrency(value)}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-end">
                        <button
                            className="flex items-center gap-2 bg-custom_green hover:bg-green-700 text-white font-bold py-2.5 px-6 rounded-xl shadow-md transition-all active:scale-95"
                            onClick={handleCancelClick}
                        >
                            <BiCheckCircle className="text-xl" /> OK
                        </button>
                    </div>
                </div>
            </div>
        </ModalPortal>
    );
}